import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { StatusTag } from "../../../components/ui";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";

const TONES = [
  { key: "professional", label: "Professional" },
  { key: "casual", label: "Casual" },
  { key: "startup", label: "Startup" },
  { key: "enterprise", label: "Enterprise" },
];

const HIRING_TYPES = ["Lateral", "Campus", "Regular"];

const STUB_LOCATIONS = [
  "Bengaluru",
  "Mumbai",
  "Delhi NCR",
  "Pune",
  "Hyderabad",
  "Chennai",
  "Remote",
  "Hybrid",
];
const STUB_RECRUITERS = ["Auto-assign", "Priya M", "Arun K", "Sneha R"];

const generateJD = ({ title, hiringType, locations, tone, openings }) => {
  const t = title || "this role";
  const loc = locations && locations.length ? locations.join(", ") : "Remote";
  const intro =
    {
      professional: `We are seeking ${openings || 1} ${t} to join our high-performing team in ${loc}.`,
      casual: `We're looking for ${openings || 1} amazing ${t} to come build with us in ${loc}.`,
      startup: `We need a scrappy ${t} who can move fast and ship in ${loc}.`,
      enterprise: `Our enterprise team is hiring ${openings || 1} ${t} for our ${loc} office to drive strategic initiatives.`,
    }[tone] || "";
  return {
    title: t,
    overview: intro,
    responsibilities: [
      `Own end-to-end delivery of ${t.toLowerCase()} workstreams.`,
      `Partner with cross-functional teams to translate requirements into shippable features.`,
      `Mentor peers and raise the technical bar through code review.`,
      `Drive measurable improvements in performance, quality, and reliability.`,
    ],
    requirements: [
      `${hiringType === "Campus" ? "Recent graduate" : "5+ years"} of relevant experience for ${t.toLowerCase()}.`,
      `Strong fundamentals in your domain plus a portfolio of shipped work.`,
      `Excellent written and verbal communication.`,
      `Bonus: experience working in fast-moving teams.`,
    ],
    perks: [
      "Competitive compensation + equity",
      "Comprehensive health + wellness benefits",
      "Learning stipend and conference budget",
      "Remote-friendly with quarterly meet-ups",
    ],
  };
};

const wordCount = (jd) => {
  if (!jd) return 0;
  const all = [
    jd.overview,
    ...(jd.responsibilities || []),
    ...(jd.requirements || []),
    ...(jd.perks || []),
  ].join(" ");
  return all.split(/\s+/).filter(Boolean).length;
};

const seoScore = (jd) => {
  if (!jd) return 0;
  const w = wordCount(jd);
  let score = 60;
  if (w > 200) score += 15;
  if (w > 350) score += 10;
  if (jd.responsibilities?.length >= 4) score += 8;
  if (jd.requirements?.length >= 4) score += 7;
  return Math.min(99, score);
};

const CreateJob = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    openings: 1,
    hiringType: "Lateral",
    locations: [],
    recruiter: "Auto-assign",
    closeDate: "",
    tone: "professional",
  });
  const [jd, setJd] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    document.title = "Create position - Ample Hire";
  }, []);

  // Hand-off from /admin/generate-jd: pick up any pending JD on mount, prefill,
  // then clear the key so a manual refresh doesn't re-apply stale data.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("ample_pending_jd");
      if (!raw) return;
      sessionStorage.removeItem("ample_pending_jd");
      const payload = JSON.parse(raw);
      if (payload?.jd) setJd(payload.jd);
      setForm((f) => ({
        ...f,
        title: payload?.title || f.title,
        locations: Array.isArray(payload?.locations)
          ? payload.locations
          : [
              ...(payload?.location ? [payload.location] : []),
              ...(payload?.remote ? ["Remote"] : []),
              ...(payload?.hybrid ? ["Hybrid"] : []),
            ],
      }));
    } catch (e) {
      // Storage blocked / corrupted JSON — fall through silently
    }
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleLocation = (loc) =>
    setForm((f) => ({
      ...f,
      locations: f.locations.includes(loc)
        ? f.locations.filter((l) => l !== loc)
        : [...f.locations, loc],
    }));

  const generate = async () => {
    if (!form.title.trim()) return;
    setGenerating(true);
    try {
      const { data } = await axiosInstance.post("/ai/jobs/generate-jd", {
        title: form.title,
        hiringType: form.hiringType,
        locations: form.locations,
        tone: form.tone,
        openings: Number(form.openings) || 1,
      });
      if (
        !data ||
        !data.overview ||
        !Array.isArray(data.responsibilities) ||
        !Array.isArray(data.requirements) ||
        !Array.isArray(data.perks)
      ) {
        throw new Error("Invalid JD response shape");
      }
      setJd(data);
    } catch (err) {
      console.warn("LLM JD generation failed, using template fallback:", err);
      setJd(generateJD(form));
    } finally {
      setGenerating(false);
    }
  };

  const [publishing, setPublishing] = useState(false);
  const publish = async () => {
    if (!jd || publishing) return;
    setPublishing(true);
    try {
      // Backend POST /job-posting/job-details/save accepts a draft payload.
      // Swallow errors non-fatally: we still advance to /admin/jobs where
      // the user can see the draft or retry. The alert that used to live here
      // was a stub — removed. Users get real network feedback via console.
      await axiosInstance.post(`${baseUrl}/job-posting/job-details/save`, {
        positionName: form.title,
        positionCounts: Number(form.openings) || 1,
        hiringType: (form.hiringType || "lateral").toLowerCase() + "_hiring",
        jobDescription: jd,
        locations: form.locations || [],
      });
    } catch (e) {
      console.error("Publish failed:", e?.response?.data || e?.message);
    } finally {
      setPublishing(false);
      navigate("/admin/jobs");
    }
  };

  const wc = wordCount(jd);
  const seo = seoScore(jd);

  return (
    <div
      className="app-content page-enter"
      style={{ maxWidth: 1280, margin: "0 auto", padding: "20px 24px 32px" }}
    >
      <div style={hdrRow}>
        <div>
          <h1 style={hdrTitle}>Create position</h1>
          <p style={hdrSub}>
            Fill basics — AI generates the full JD instantly.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={btnGhost}>Save draft</button>
          <button
            onClick={publish}
            disabled={!jd}
            style={{
              ...btnPrimary,
              opacity: jd ? 1 : 0.5,
              cursor: jd ? "pointer" : "not-allowed",
            }}
          >
            Publish job
          </button>
        </div>
      </div>

      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1.1fr", gap: 16 }}
      >
        {/* LEFT — form */}
        <motion.div
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          style={card}
        >
          <div style={cardLabel}>Position basics</div>
          <Field label="Job title">
            <input
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="e.g. Senior Backend Engineer"
              style={input}
            />
          </Field>
          <Row>
            <Field label="Openings">
              <input
                type="number"
                min="1"
                value={form.openings}
                onChange={(e) =>
                  update("openings", parseInt(e.target.value || "1", 10))
                }
                style={input}
              />
            </Field>
            <Field label="Type">
              <select
                value={form.hiringType}
                onChange={(e) => update("hiringType", e.target.value)}
                style={input}
              >
                {HIRING_TYPES.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </Field>
          </Row>
          <Field label="Locations (multi-select)">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {STUB_LOCATIONS.map((l) => {
                const active = form.locations.includes(l);
                return (
                  <button
                    key={l}
                    onClick={() => toggleLocation(l)}
                    style={{
                      padding: "5px 12px",
                      borderRadius: 100,
                      fontSize: 11,
                      fontWeight: 600,
                      border: active
                        ? "1px solid #7B5AC4"
                        : "1px solid #E2E8F0",
                      background: active ? "#EEF2FF" : "white",
                      color: active ? "#4338CA" : "#64748B",
                      cursor: "pointer",
                    }}
                  >
                    {l}
                  </button>
                );
              })}
            </div>
          </Field>
          <Row>
            <Field label="Recruiter">
              <select
                value={form.recruiter}
                onChange={(e) => update("recruiter", e.target.value)}
                style={input}
              >
                {STUB_RECRUITERS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Close date">
              <input
                type="date"
                value={form.closeDate}
                onChange={(e) => update("closeDate", e.target.value)}
                style={input}
              />
            </Field>
          </Row>
          <Field label="JD tone">
            <div style={{ display: "flex", gap: 6 }}>
              {TONES.map((t) => {
                const active = form.tone === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => update("tone", t.key)}
                    style={{
                      flex: 1,
                      padding: "7px 12px",
                      borderRadius: 100,
                      fontSize: 11,
                      fontWeight: 600,
                      border: active
                        ? "1px solid #7B5AC4"
                        : "1px solid #E2E8F0",
                      background: active ? "#EEF2FF" : "white",
                      color: active ? "#4338CA" : "#64748B",
                      cursor: "pointer",
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <button
            onClick={generate}
            disabled={generating || !form.title.trim()}
            style={{
              ...btnPrimary,
              width: "100%",
              marginTop: 8,
              opacity: generating || !form.title.trim() ? 0.5 : 1,
              cursor:
                generating || !form.title.trim() ? "not-allowed" : "pointer",
            }}
          >
            {generating
              ? "Generating…"
              : jd
                ? "Regenerate JD with AI"
                : "Generate JD with AI →"}
          </button>
          <div style={{ marginTop: 8, textAlign: "center", fontSize: 11 }}>
            <button
              type="button"
              onClick={() => navigate("/admin/generate-jd")}
              style={{
                background: "none",
                border: "none",
                color: "#7B5AC4",
                cursor: "pointer",
                fontSize: 11,
                textDecoration: "underline",
                padding: 0,
              }}
            >
              Edit in JD generator →
            </button>
          </div>
        </motion.div>

        {/* RIGHT — AI JD preview */}
        <motion.div
          initial={{ opacity: 0, x: 8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          style={card}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <div style={cardLabel}>AI preview</div>
            {jd && (
              <div style={{ display: "flex", gap: 6 }}>
                <StatusTag type="green" text={`SEO ${seo}/100`} />
                <StatusTag type="blue" text={`${wc} words`} />
              </div>
            )}
          </div>

          {!jd && !generating && (
            <div
              style={{
                padding: "40px 16px",
                textAlign: "center",
                color: "#94A3B8",
              }}
            >
              <div style={{ fontSize: 36, marginBottom: 8 }}>🪄</div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#475569",
                  marginBottom: 4,
                }}
              >
                Your AI-generated JD will appear here
              </div>
              <div style={{ fontSize: 11 }}>
                Fill in the basics on the left and hit "Generate JD with AI"
              </div>
            </div>
          )}
          {generating && (
            <div
              style={{
                padding: 24,
                textAlign: "center",
                color: "#64748B",
                fontSize: 12,
              }}
            >
              AI is drafting your job description…
            </div>
          )}

          {jd && (
            <>
              <div style={{ padding: "8px 0" }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#0F172A",
                    marginBottom: 6,
                  }}
                >
                  {jd.title}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#475569",
                    lineHeight: 1.5,
                    marginBottom: 12,
                  }}
                >
                  {jd.overview}
                </div>

                <Section
                  heading="Responsibilities"
                  items={jd.responsibilities}
                />
                <Section heading="Requirements" items={jd.requirements} />
                <Section heading="Perks" items={jd.perks} />
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button onClick={generate} style={btnGhost}>
                  Regenerate
                </button>
                <button style={btnGhost}>Edit</button>
                <button
                  onClick={publish}
                  style={{ ...btnPrimary, marginLeft: "auto" }}
                >
                  Publish job →
                </button>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

const Section = ({ heading, items }) => (
  <div style={{ marginBottom: 12 }}>
    <div
      style={{
        fontSize: 10,
        color: "#94A3B8",
        textTransform: "uppercase",
        letterSpacing: 0.5,
        marginBottom: 4,
        fontWeight: 600,
      }}
    >
      {heading}
    </div>
    <ul
      style={{
        margin: 0,
        paddingLeft: 18,
        fontSize: 11,
        color: "#334155",
        lineHeight: 1.6,
      }}
    >
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  </div>
);

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <div
      style={{
        fontSize: 10,
        color: "#64748B",
        marginBottom: 4,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.4,
      }}
    >
      {label}
    </div>
    {children}
  </div>
);
const Row = ({ children }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 10,
      marginBottom: 0,
    }}
  >
    {children}
  </div>
);

const card = {
  background: "white",
  border: "1px solid #E2E8F0",
  borderRadius: 12,
  padding: 16,
};
const cardLabel = {
  fontSize: 11,
  fontWeight: 700,
  color: "#94A3B8",
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginBottom: 12,
};
const input = {
  width: "100%",
  padding: "8px 12px",
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  fontSize: 13,
  color: "#0F172A",
  outline: "none",
  background: "white",
  boxSizing: "border-box",
};
const hdrRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  marginBottom: 16,
};
const hdrTitle = { fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 };
const hdrSub = { fontSize: 13, color: "#64748B", margin: "4px 0 0" };
const btnPrimary = {
  padding: "9px 18px",
  borderRadius: 6,
  background: "#7B5AC4",
  color: "white",
  border: "none",
  fontWeight: 600,
  fontSize: 13,
  cursor: "pointer",
};
const btnGhost = {
  padding: "8px 14px",
  borderRadius: 6,
  background: "white",
  color: "#475569",
  border: "1px solid #E2E8F0",
  fontWeight: 600,
  fontSize: 12,
  cursor: "pointer",
};

export default CreateJob;
